import { useTranslations } from '@vocab/react';
import {
  Actions,
  Box,
  Button,
  Dialog,
  List,
  Stack,
  Text,
} from 'braid-design-system';
import { type FC, useId } from 'react';

import { TrackOnMount } from 'src/libs/Analytics';

import translations from './.vocab';

interface Props {
  onClose: () => void;
  open: boolean;
  referenceBalance: {
    availableBalance: number;
    totalBalance: number;
  };
  trackReferenceCheckBalanceDialogDisplayedEvent: () => void;
  trackReferenceCheckBalanceDialogDismissedEvent: () => void;
}

export const ReferenceCheckBalanceDialog: FC<Props> = ({
  onClose,
  open,
  referenceBalance,
  trackReferenceCheckBalanceDialogDisplayedEvent,
  trackReferenceCheckBalanceDialogDismissedEvent,
}) => {
  const { t } = useTranslations(translations);
  const id = useId();

  const handleClose = () => {
    trackReferenceCheckBalanceDialogDismissedEvent();
    onClose();
  };

  const hasReferenceChecksLeft = referenceBalance.availableBalance > 0;
  return (
    <Dialog
      id={id}
      closeLabel={t('Close')}
      onClose={handleClose}
      open={open}
      title={
        hasReferenceChecksLeft
          ? t('How reference checks work')
          : t('No reference checks left')
      }
    >
      <TrackOnMount onMount={trackReferenceCheckBalanceDialogDisplayedEvent}>
        <Stack
          space="large"
          data={{ testid: 'reference-check-balance-dialog-content' }}
        >
          <Box paddingY="xxsmall">
            {hasReferenceChecksLeft ? (
              <List>
                <Text>
                  {t('You can check references for up to 3 applicants.')}
                </Text>
                <Text>
                  {t(
                    "To check an applicant's references, select the Check References button.",
                  )}
                </Text>
                <Text>
                  {t(
                    "We'll contact 2 referees for each applicant and ask them a set of questions.",
                  )}
                </Text>
                <Text>
                  {t(
                    "We'll let you know when we receive a response. Referees may respond at different times.",
                  )}
                </Text>
                <Text>
                  {t(
                    'You have 3 months from the date of purchase to use your reference checks.',
                  )}
                </Text>
              </List>
            ) : (
              <Stack space="large">
                <Text>{t("You've used all your reference checks.")}</Text>
                <Text>
                  {t(
                    "Right now, you can only buy reference checks when you post a job ad. We're working on letting you buy more reference checks when your job ad is live.",
                  )}
                </Text>
              </Stack>
            )}
          </Box>
          <Actions>
            <Button onClick={handleClose} tone="brandAccent">
              {t('Close')}
            </Button>
          </Actions>
        </Stack>
      </TrackOnMount>
    </Dialog>
  );
};
